import React, { createContext, useEffect, useState, useReducer, useContext, useCallback, useRef } from 'react'
import { getCachedAuthSession } from 'services/authSessionCache'
import Environment from 'helpers/environment'

const WebsocketContext = createContext<any>(null) // eslint-disable-line @typescript-eslint/no-explicit-any

export const WebsocketContextProvider = ({ children }: { children: React.ReactNode }) => {
  const clientRef = useRef<WebSocket | null>(null)
  const [closed, forceClose] = useReducer(() => true, false)
  const [payloads, setPayloads] = useState<Record<string, unknown>>({})
  const subscriptionsRef = useRef<Set<string>>(new Set())
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    initializeClient()
    return () => {
      if (clientRef.current != null) {
        forceClose()
        clientRef.current.close()
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const unsubscribe = useCallback((entityId: string, topic: string) => {
    const subscriptionKey = `${entityId}-${topic}`
    subscriptionsRef.current.delete(subscriptionKey)

    if (clientRef.current != null && clientRef.current.readyState === WebSocket.OPEN) {
      clientRef.current.send(JSON.stringify({
        'operation': 'unsubscribe',
        topic,
        entityId
      }))
    }
  }, [])

  const subscribe = useCallback(async (entityId: string, topic: string) => {
    const currentSession = await getCachedAuthSession()
    const idToken = currentSession.tokens?.idToken
    const subscriptionKey = `${entityId}-${topic}`
    subscriptionsRef.current.add(subscriptionKey)

    if (clientRef.current != null && clientRef.current.readyState === WebSocket.OPEN) {
      clientRef.current.send(JSON.stringify({
        'operation': 'subscribe',
        topic,
        entityId,
        'idToken': idToken?.toString()
      }))
    } else {
      console.log('WebSocket not open, subscription queued for reconnect:', subscriptionKey)
    }
  }, [])

  const sendHeartbeat = useCallback(() => {
    if (clientRef.current != null && clientRef.current.readyState === WebSocket.OPEN) {
      console.log('Sending heartbeat')
      clientRef.current.send(JSON.stringify({
        'operation': 'heartbeat'
      }))
    }
  }, [])

  const initializeClient = async () => {
    const currentSession = await getCachedAuthSession()
    const idToken = currentSession.tokens?.idToken
    if (!idToken) {
      return false
    }

    const newClient = new WebSocket(`${Environment.webSocketURL()}?idToken=${idToken}`)

    newClient.onopen = () => {
      console.log('WebSocket connected')
      console.log('Resubscribing to', subscriptionsRef.current.size, 'subscriptions')

      // Resubscribe to all active subscriptions using the ref
      if (newClient.readyState === WebSocket.OPEN) {
        subscriptionsRef.current.forEach((subscription: string) => {
          const [entityId, topic] = subscription.split('-')
          console.log('Resubscribing to:', entityId, topic)
          newClient.send(JSON.stringify({
            'operation': 'subscribe',
            topic,
            entityId,
            'idToken': idToken.toString()
          }))
        })
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 1000 * 60 * 3) // 3 minutes
    }

    newClient.onerror = () => {
      console.log('WebSocket error:')

      setTimeout(async () => {
        try {
          await initializeClient()
        } catch (error) {
          console.log('Failed to reinitialize WebSocket:', error)
        }
      }, 1000)
    }

    newClient.onclose = () => {
      console.log('WebSocket closed')
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      if (!closed) {
        setTimeout(async () => {
          try {
            await initializeClient()
          } catch (error) {
            console.log('Failed to reinitialize WebSocket:', error)
          }
        }, 1000)
      }
    }

    newClient.onmessage = async (message: MessageEvent) => {
      setPayloads((prevPayloads) => {
        const updatedPayloads: Record<string, unknown> = { ...prevPayloads }
        const data = JSON.parse(message.data)
        console.log('WebSocket message received:', data)
        updatedPayloads[`${data.deviceId}-${data.topic}`] = data
        return updatedPayloads
      })
    }

    clientRef.current = newClient
  }

  return (
    <WebsocketContext.Provider value={{ subscribe, payloads, unsubscribe }}>
      {children}
    </WebsocketContext.Provider>
  )
}

export const useWebsocketContext = (entityId?: string | null, topic?: string | null) => {
  const context = useContext(WebsocketContext)

  if (!entityId || !topic) {
    return context
  }

  const payloadKey = `${entityId}-${topic}`
  const filteredPayload = context.payloads[payloadKey] ?? null

  return {
    ...context,
    payload: filteredPayload
  }
}
